import type { DifficultyId } from '../combat/types/primitives'
import type {
  CampaignDefinition,
  ChapterDefinition,
  StageDefinition,
  StageWaveSpec
} from '../combat/campaign/types'
import type { MapDefinition, WaveDefinition, WaveGroupDefinition } from '../model/types'
import { getMap } from '../model/mapCatalog'
import { TOWER_CATALOG, type TowerDefinition } from '../model/towers'
import { GameSession, type GameSessionOptions } from '../model/session'
import { resolveDifficultyRules, type DifficultyResolveMode } from '../combat/difficulty/catalog'
import {
  MAIN_CAMPAIGN,
  CHAPTER_BY_ID,
  STAGE_BY_ID,
  listChapterStages,
  getChapterForStage,
  listChapters
} from './registry'

/** Immutable identity attached to a campaign GameSession. */
export interface CampaignSessionContext {
  campaignId: string
  chapterId: string
  stageId: string
  difficultyId: DifficultyId
  mapId: string
  displayLabel: string
  /** Tower allow-list for this stage (undefined = all catalog towers). */
  allowedTowerIds: string[] | undefined
}

export interface CreateCampaignSessionInput {
  stageId: string
  difficultyId?: DifficultyId
  /** `legacy` keeps identity scaling (M47/M48 authoring baseline). */
  difficultyMode?: DifficultyResolveMode
  enemies?: GameSessionOptions['enemies']
  towers?: GameSessionOptions['towers']
  /**
   * Research snapshot for this match. Caller supplies; never read from storage here.
   * Omitted → R0 (empty ranks).
   */
  researchSnapshot?: GameSessionOptions['researchSnapshot']
}

export interface CreateCampaignSessionResult {
  session: GameSession
  context: CampaignSessionContext
  map: MapDefinition
  stage: StageDefinition
}

export function getCampaign(campaignId: string): CampaignDefinition | undefined {
  if (campaignId === MAIN_CAMPAIGN.id) return MAIN_CAMPAIGN
  return undefined
}

export function getChapter(chapterId: string): ChapterDefinition | undefined {
  return CHAPTER_BY_ID[chapterId]
}

export function getStage(stageId: string): StageDefinition | undefined {
  return STAGE_BY_ID[stageId]
}

/** @deprecated Prefer listChapterStages(chapterId). */
export function listChapter1Stages(): StageDefinition[] {
  return listChapterStages('chapter-1')
}

export function stageWavesToMapWaves(waves: StageWaveSpec[]): WaveDefinition[] {
  return waves.map((wave) => ({
    id: wave.id,
    completionReward: wave.completionReward,
    groups: wave.groups.map(
      (g): WaveGroupDefinition => ({
        enemy: g.enemyId,
        count: g.count,
        interval: g.interval,
        delay: g.delay,
        enemyLevel: g.enemyLevel,
        enemyRank: g.enemyRank,
        ...(g.routeId !== undefined ? { routeId: g.routeId } : {})
      })
    )
  }))
}

/**
 * Build a playable MapDefinition for a stage without mutating the catalog map.
 * Stage owns waves; map owns geometry. startingGold/lives may come from difficulty.
 */
export function materializeStageMap(
  stage: StageDefinition,
  options?: {
    difficultyId?: DifficultyId
    difficultyMode?: DifficultyResolveMode
  }
): MapDefinition {
  const base = getMap(stage.mapId)
  if (!base) {
    throw new Error(`Unknown mapId for stage ${stage.id}: ${stage.mapId}`)
  }
  const difficultyId = options?.difficultyId ?? 'normal'
  const mode = options?.difficultyMode ?? 'provisional'
  const rules = resolveDifficultyRules(difficultyId, mode)
  const clone = structuredClone(base) as MapDefinition
  clone.waves = stageWavesToMapWaves(stage.waves)
  if (mode === 'provisional') {
    clone.startingGold = rules.startingCoins
  }
  return clone
}

export function filterTowerCatalog(
  allowedTowerIds: string[] | undefined,
  full: Record<string, TowerDefinition> = TOWER_CATALOG
): Record<string, TowerDefinition> {
  if (!allowedTowerIds || allowedTowerIds.length === 0) return { ...full }
  const out: Record<string, TowerDefinition> = {}
  for (const id of allowedTowerIds) {
    const def = full[id]
    if (def) out[id] = def
  }
  return out
}

/**
 * Authoritative campaign session factory.
 * GameSession does not navigate chapters — it only receives immutable context.
 */
export function createCampaignSession(
  input: CreateCampaignSessionInput
): CreateCampaignSessionResult {
  const stage = getStage(input.stageId)
  if (!stage) throw new Error(`Unknown stageId: ${input.stageId}`)
  const difficultyId = input.difficultyId ?? 'normal'
  if (!stage.availableDifficulties.includes(difficultyId)) {
    throw new Error(`Difficulty ${difficultyId} not available on stage ${stage.id}`)
  }
  const mode = input.difficultyMode ?? 'provisional'
  const map = materializeStageMap(stage, { difficultyId, difficultyMode: mode })
  const towers = filterTowerCatalog(stage.allowedTowerIds, input.towers ?? TOWER_CATALOG)
  const difficultyPolicy = resolveDifficultyRules(difficultyId, mode)
  const context: CampaignSessionContext = {
    campaignId: MAIN_CAMPAIGN.id,
    chapterId: stage.chapterId,
    stageId: stage.id,
    difficultyId,
    mapId: stage.mapId,
    displayLabel: stage.displayLabel,
    allowedTowerIds: stage.allowedTowerIds ? [...stage.allowedTowerIds] : undefined
  }
  const session = new GameSession(map, {
    enemies: input.enemies,
    towers,
    campaignContext: context,
    difficultyPolicy,
    researchSnapshot: input.researchSnapshot
  })
  return { session, context, map, stage }
}

/**
 * Next stage in chapter, or first stage of the following chapter.
 * Unlock policy is separate (see campaignProgress.isStageUnlocked).
 */
export function nextStageId(stageId: string): string | null {
  const chapter = getChapterForStage(stageId)
  if (!chapter) return null
  const idx = chapter.stageIds.indexOf(stageId)
  if (idx < 0) return null
  if (idx < chapter.stageIds.length - 1) {
    return chapter.stageIds[idx + 1] ?? null
  }
  const chapters = listChapters(chapter.campaignId)
  const chIdx = chapters.findIndex((c) => c.id === chapter.id)
  if (chIdx < 0 || chIdx >= chapters.length - 1) return null
  const nextChapter = chapters[chIdx + 1]!
  return nextChapter.stageIds[0] ?? null
}

export { listChapterStages, listChapters, getChapterForStage }
